import { useMemo, useState } from 'react'

import { SecondaryActionItem } from '@factorialco/f0-react/dist/experimental'
import { ArrowCycle, Upload } from '@factorialco/f0-react/icons/app'

import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { Modals, ModalsProps } from 'modules/integrations/components/SyncButton/Modals'
import { usePrivateConditionalIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'

export type UseSyncActionProps = {
  onConfirmSync?: ModalsProps['onConfirm']
  confirmTrackingEvent?: string
  disabled?: boolean
}

type UseSyncActionReturn = {
  dataCollectionSyncAction: SecondaryActionItem[]
  syncModal: React.ReactNode | null
}

export const useSyncAction = ({
  onConfirmSync,
  confirmTrackingEvent,
  disabled = false,
}: UseSyncActionProps = {}): UseSyncActionReturn => {
  const context = usePrivateConditionalIntegrationsSyncContext(disabled)
  const capabilityMode = context?.capabilityMode
  const syncEnabled = context?.syncEnabled ?? false
  const marketplaceIntegration = context?.marketplaceIntegration
  const isSyncRunning = context?.isSyncRunning ?? false
  const [showModal, setShowModal] = useState(false)

  const isExportMode = capabilityMode === 'file'
  const canShowSyncAction = !disabled && syncEnabled && marketplaceIntegration

  const dataCollectionSyncAction = useMemo<SecondaryActionItem[]>(() => {
    if (canShowSyncAction) {
      return [
        {
          label: isExportMode
            ? i18n.t('compensations.integrations.export_action', {
                integration_name: marketplaceIntegration.name,
              })
            : i18n.t('compensations.integrations.sync_action', {
                integration_name: marketplaceIntegration.name,
              }),
          icon: isExportMode ? Upload : ArrowCycle,
          loading: isSyncRunning,
          onClick: () => {
            setShowModal(true)

            if (confirmTrackingEvent) {
              track(confirmTrackingEvent)
            }
          },
        },
      ]
    } else {
      return []
    }
  }, [
    disabled,
    canShowSyncAction,
    marketplaceIntegration,
    isExportMode,
    isSyncRunning,
    confirmTrackingEvent,
  ])
  return {
    dataCollectionSyncAction,
    syncModal: showModal ? <Modals setShowModal={setShowModal} onConfirm={onConfirmSync} /> : null,
  }
}
